/**
 * Excel模板生成工具
 */

import * as XLSX from 'xlsx';
import { TrainingStandardFormData } from '../types';

/**
 * 生成培训标准导入模板
 */
export const generateTrainingStandardTemplate = (): void => {
  const templateData = [
    // 表头
    [
      '培训业务名称',
      '培训要素名称',
      '城市等级',
      '最小值',
      '最大值',
      '计量单位',
      '是否启用',
      '备注说明'
    ],
    // 示例数据
    ['安全培训', '培训师费', '', 100.00, 300.00, '元/课时', '启用', '适用于所有城市等级的基础安全培训师资费用标准'],
    ['安全培训', '场地费', '1', 200.00, 500.00, '元/天', '启用', '一线城市场地费用标准'],
    ['技能培训', '设备费', '', 150.00, 400.00, '元/人次', '启用', '技能培训设备费（通用标准）'],
    ['技能培训', '教材费', '2', 50.00, 150.00, '元/套', '启用', '二线城市技能培训教材标准'],
    ['管理培训', '咨询费', '', 800.00, 1500.00, '元/天', '启用', '管理咨询通用标准（适用所有城市等级）']
  ];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(templateData);
  
  // 设置列宽
  worksheet['!cols'] = [
    { width: 18 },  // 培训业务名称
    { width: 18 },  // 培训要素名称
    { width: 12 },  // 城市等级
    { width: 12 },  // 最小值
    { width: 12 },  // 最大值
    { width: 15 },  // 计量单位
    { width: 12 },  // 是否启用
    { width: 30 }   // 备注说明
  ];

  // 设置标题行样式（如果浏览器支持）
  try {
    const titleCell = 'A1';
    if (!worksheet[titleCell]) worksheet[titleCell] = { v: '' };
    worksheet[titleCell].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: 'center' }
    };
  } catch (e) {
    // 忽略样式设置错误
  }

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '培训标准导入模板');

  // 生成Excel文件并下载
  XLSX.writeFile(workbook, '培训标准导入模板.xlsx');
};

/**
 * 生成检查数据导入模板
 */
export const generateInspectionDataTemplate = (): void => {
  const templateData = [
    // 标题行
    ['培训标准化检查结果-模板'],
    [],
    // 公司信息表头
    ['业务所属创大', '主体分级', '负责人'],
    // 北京华普示例
    ['北京华普', 1, '张三'],
    // 培训业务表头
    ['培训业务名称', '培训要素', '本次检查结果', '单位'],
    // GYB培训示例数据
    ['GYB培训', '师资费用', 700.00, '元'],
    ['GYB培训', '招生费用', 350.00, '元'], 
    ['GYB培训', '场地费用', 3689.00, '元'],
    ['GYB培训', '教材费用', 345.00, '元'],
    ['GYB培训', '参访费用', 678.00, '元'],
    ['GYB培训', '设备费用', 456.00, '元'],
    ['GYB培训', '物料费用', 345.00, '元'],
    [],
    // SYC学员班示例数据
    ['SYC学员班', '师资费用', 1200.00, '元'],
    ['SYC学员班', '招生费用', 800.00, '元'],
    ['SYC学员班', '场地费用', 500.00, '元'],
    ['SYC学员班', '教材费用', 600.00, '元'],
    ['SYC学员班', '参访费用', 400.00, '元'],
    ['SYC学员班', '设备费用', 300.00, '元'],
    ['SYC学员班', '物料费用', 89.00, '元'],
    [],
    // 说明
    ['说明：'],
    ['1. 业务所属创大：填写具体的公司名称'],
    ['2. 主体分级：1=一线城市, 2=二线城市, 3=三线城市, 4=四线城市'],
    ['3. 培训业务名称：如GYB培训、SYC学员班、军人适应性培训等'],
    ['4. 培训要素：如师资费用、场地费用、教材费用等'],
    ['5. 本次检查结果：填写实际的费用数值'],
    ['6. 单位：费用的计量单位，如元、课时、天等']
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(templateData);
  
  // 设置列宽
  worksheet['!cols'] = [
    { width: 15 },  // 业务所属创大
    { width: 10 },  // 主体分级  
    { width: 10 },  // 负责人
    { width: 20 },  // 培训业务名称
    { width: 15 },  // 培训要素
    { width: 15 },  // 本次检查结果
    { width: 10 }   // 单位
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '检查数据导入模板');

  XLSX.writeFile(workbook, '检查数据导入模板.xlsx');
};

/**
 * 解析培训标准Excel文件
 */
export interface ParseResult {
  success: boolean;
  data: TrainingStandardFormData[];
  errors: string[];
  warnings: string[];
}

export const parseTrainingStandardExcel = async (
  file: File,
  businessMapping: Map<string, number>,  // 业务名称到ID的映射
  elementMapping: Map<string, number>    // 要素名称到ID的映射
): Promise<ParseResult> => {
  try {
    // 读取Excel文件
    const fileBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(fileBuffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    
    if (!worksheet) {
      return {
        success: false,
        data: [],
        errors: ['Excel文件格式错误'],
        warnings: []
      };
    }

    // 转换为JSON数据，第1行为表头，从第2行开始读取数据
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      range: 1, // 从第2行开始（0-based index，跳过第1行表头）
      header: ['培训业务名称', '培训要素名称', '城市等级', '最小值', '最大值', '计量单位', '是否启用', '备注说明'],
      defval: ''
    }) as any[];

    if (!jsonData || jsonData.length === 0) {
      return {
        success: false,
        data: [],
        errors: ['Excel文件中没有找到有效数据'],
        warnings: []
      };
    }

    // 过滤空行和说明行
    const validRows = jsonData.filter(row => {
      const businessName = String(row['培训业务名称'] || '').trim();
      const elementName = String(row['培训要素名称'] || '').trim();
      const minValue = row['最小值'];
      const maxValue = row['最大值'];
      
      // 跳过说明行和空行
      if (businessName.includes('字段说明') || businessName.includes('重要提示') || businessName.includes('说明')) {
        return false;
      }
      
      return businessName && elementName && minValue !== '' && maxValue !== '';
    });

    if (validRows.length === 0) {
      return {
        success: false,
        data: [],
        errors: ['没有找到完整的数据行（培训业务名称、培训要素名称、最小值、最大值为必填项）'],
        warnings: []
      };
    }

    // 验证和转换数据
    const errors: string[] = [];
    const warnings: string[] = [];
    const validData: TrainingStandardFormData[] = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const rowNumber = i + 2; // 实际Excel行号（从第2行开始）

      try {
        const validationResult = validateTrainingStandardRow(row, rowNumber, businessMapping, elementMapping);
        
        if (validationResult.isValid && validationResult.data) {
          validData.push(validationResult.data);
        } else {
          errors.push(...validationResult.errors);
        }
        
        if (validationResult.warnings) {
          warnings.push(...validationResult.warnings);
        }
      } catch (error) {
        errors.push(`第${rowNumber}行: ${error instanceof Error ? error.message : '数据验证失败'}`);
      }
    }

    return {
      success: validData.length > 0,
      data: validData,
      errors,
      warnings
    };

  } catch (error) {
    return {
      success: false,
      data: [],
      errors: [error instanceof Error ? error.message : 'Excel文件解析失败'],
      warnings: []
    };
  }
};

/**
 * 验证单行数据
 */
interface ValidationResult {
  isValid: boolean;
  data?: TrainingStandardFormData;
  errors: string[];
  warnings?: string[];
}

const validateTrainingStandardRow = (
  row: any,
  rowNumber: number,
  businessMapping: Map<string, number>,
  elementMapping: Map<string, number>
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    const businessName = String(row['培训业务名称'] || '').trim();
    const elementName = String(row['培训要素名称'] || '').trim();
    const cityLevel = parseCityLevel(row['城市等级']);
    const minRange = parseNumber(row['最小值']);
    const maxRange = parseNumber(row['最大值']);
    const unit = String(row['计量单位'] || '').trim();
    const isActive = parseActiveStatus(row['是否启用']);
    const remarks = String(row['备注说明'] || '').trim();

    // 验证必填字段
    if (!businessName) {
      errors.push(`第${rowNumber}行: 培训业务名称不能为空`);
    }
    if (!elementName) {
      errors.push(`第${rowNumber}行: 培训要素名称不能为空`);
    }
    if (!unit) {
      errors.push(`第${rowNumber}行: 计量单位不能为空`);
    }
    if (isNaN(minRange)) {
      errors.push(`第${rowNumber}行: 最小值必须是有效数字`);
    }
    if (isNaN(maxRange)) {
      errors.push(`第${rowNumber}行: 最大值必须是有效数字`);
    }

    // 验证业务和要素是否存在
    const businessId = businessMapping.get(businessName);
    if (!businessId) {
      errors.push(`第${rowNumber}行: 培训业务"${businessName}"不存在，请先在系统中创建`);
    }

    const elementId = elementMapping.get(elementName);
    if (!elementId) {
      errors.push(`第${rowNumber}行: 培训要素"${elementName}"不存在，请先在系统中创建`);
    }

    // 验证数值范围
    if (!isNaN(minRange) && !isNaN(maxRange)) {
      if (minRange < 0) {
        errors.push(`第${rowNumber}行: 最小值不能小于0`);
      }
      if (maxRange < 0) {
        errors.push(`第${rowNumber}行: 最大值不能小于0`);
      }
      if (minRange > maxRange) {
        errors.push(`第${rowNumber}行: 最小值不能大于最大值`);
      }
    }

    // 验证城市等级
    if (cityLevel !== undefined && ![1, 2, 3, 4, 5].includes(cityLevel)) {
      errors.push(`第${rowNumber}行: 城市等级必须是1、2、3、4、5中的一个，或留空`);
    }

    if (errors.length > 0) {
      return { isValid: false, errors, warnings };
    }

    const data: TrainingStandardFormData = {
      business_id: businessId!,
      element_id: elementId!,
      city_level: cityLevel,
      min_range: minRange,
      max_range: maxRange,
      unit: unit,
      remarks: remarks || undefined,
      is_active: isActive
    };

    return { isValid: true, errors: [], warnings, data };

  } catch (error) {
    return {
      isValid: false,
      errors: [`第${rowNumber}行: ${error instanceof Error ? error.message : '数据格式错误'}`],
      warnings
    };
  }
};

// 辅助函数
const parseCityLevel = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numValue = Number(value);
  if (!isNaN(numValue) && [1, 2, 3, 4, 5].includes(numValue)) {
    return numValue;
  }

  const stringValue = String(value).trim();
  const match = stringValue.match(/[1-5]/);
  if (match) {
    const parsed = parseInt(match[0]);
    return [1, 2, 3, 4, 5].includes(parsed) ? parsed : undefined;
  }

  return undefined;
};

const parseActiveStatus = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return 1; // 默认启用
  }

  const stringValue = String(value).trim().toLowerCase();
  if (stringValue === '停用' || stringValue === '禁用' || stringValue === 'false' || stringValue === '0') {
    return 0;
  }
  
  return 1; // 默认启用
};

const parseNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }

  if (typeof value === 'number') {
    return value;
  }

  const stringValue = String(value).trim();
  const cleanedValue = stringValue
    .replace(/[,，]/g, '')  // 移除逗号
    .replace(/[元]/g, '')   // 移除单位
    .replace(/[^0-9.-]/g, ''); // 只保留数字、点和负号

  const numValue = parseFloat(cleanedValue);
  return isNaN(numValue) ? NaN : numValue;
};